import React, {
  createContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// third-party
import { jwtDecode } from "jwt-decode";

// reducer - state management
import { LOGIN, LOGOUT } from "contexts/auth-reducer/actions";
import authReducer from "contexts/auth-reducer/auth";

// project imports
import Loader from "components/Loader";
import axios from "utils/axios";
import { KeyedObject } from "types/root";
import { AuthProps, JWTContextType, RegisterOptions } from "types/auth";
import {
  listAirports,
  loginUser,
  logoutUser,
  redirectToLoginForAuthFailure,
  registerUser,
  selectGeneralAdminSession,
  selectSessionAirport,
  type AirportRecord,
  type AuthUserRecord,
} from "api/auth";
import { GENERAL_ADMIN_SCOPE_VALUE } from "constants/airportScope";

const TOKEN_STORAGE_KEY = "serviceToken";
const USER_STORAGE_KEY = "serviceUser";
const ADMIN_AIRPORT_STORAGE_KEY = "serviceAdminAirportSelection";
const SESSION_TOKEN_CHANGED_EVENT = "runway:session-token-changed";
const WEB_APP_ALLOWED_POSITIONS = new Set([
  "super_admin",
  "admin",
  "manager",
  "lead_supervisor",
]);
const WEB_APP_ACCESS_ERROR =
  "Web app access is restricted to admin, lead_supervisor, and manager roles.";
const MAX_JAVASCRIPT_TIMEOUT_MS = 2_147_000_000;

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

type AdminAirportSelection = {
  userId: string;
  scope: "airport" | "general";
  airportId?: string | null;
  airportName?: string | null;
  airportTimezone?: string | null;
};

const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) return false;
  const decoded: KeyedObject = jwtDecode(serviceToken);
  return Boolean(decoded.exp && decoded.exp > Date.now() / 1000);
};

const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem(TOKEN_STORAGE_KEY, serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    delete axios.defaults.headers.common.Authorization;
  }
  window.dispatchEvent(new Event(SESSION_TOKEN_CHANGED_EVENT));
};

const setStoredUser = (user?: KeyedObject | null) => {
  if (!user) {
    localStorage.removeItem(USER_STORAGE_KEY);
    return;
  }
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
};

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setStoredAdminAirportSelection = (
  selection?: AdminAirportSelection | null,
) => {
  if (
    !selection?.userId ||
    (selection.scope === "airport" && !selection.airportId)
  ) {
    localStorage.removeItem(ADMIN_AIRPORT_STORAGE_KEY);
    return;
  }
  localStorage.setItem(ADMIN_AIRPORT_STORAGE_KEY, JSON.stringify(selection));
};

const readStoredAdminAirportSelection = () => {
  try {
    const raw = localStorage.getItem(ADMIN_AIRPORT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AdminAirportSelection>;
    if (!parsed.userId) return null;
    if (parsed.scope === "general") {
      return {
        userId: parsed.userId,
        scope: "general",
      } as AdminAirportSelection;
    }
    if (parsed.airportId) {
      return {
        ...parsed,
        scope: "airport",
        airportId: parsed.airportId,
      } as AdminAirportSelection;
    }
    return null;
  } catch {
    return null;
  }
};

function getPositionInfo(position: AuthUserRecord["position"]) {
  if (!position) return { positionKey: undefined, positionLabel: undefined };
  if (typeof position === "string")
    return { positionKey: position, positionLabel: position };
  return {
    positionKey: position.key,
    positionLabel: position.label || position.key,
  };
}

function getAirportInfo(airport: AuthUserRecord["airport"]) {
  if (!airport)
    return { airportId: null, airportName: null, airportTimezone: null };
  if (typeof airport === "string")
    return { airportId: airport, airportName: airport, airportTimezone: null };
  return {
    airportId: airport._id,
    airportName: airport.name || airport.code || airport._id,
    airportTimezone: airport.timezone || null,
  };
}

function normalizeUser(user: AuthUserRecord) {
  const { positionKey, positionLabel } = getPositionInfo(user.position);
  const { airportId, airportName, airportTimezone } = getAirportInfo(
    user.airport,
  );
  const preferredName = user.preferredName || user.firstName || "";
  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    id: user._id,
    firstName: user.firstName,
    lastName: user.lastName,
    preferredName,
    employee_id: user.employee_id,
    name: preferredName || fullName || user.employee_id || user.email || "User",
    email: user.email || undefined,
    role: positionLabel || positionKey,
    positionKey,
    positionLabel,
    airportId,
    airportName,
    airportTimezone,
  };
}

function hasWebAppAccess(positionKey: unknown) {
  return WEB_APP_ALLOWED_POSITIONS.has(String(positionKey || "").toLowerCase());
}

function isAdminPosition(positionKey: unknown) {
  return String(positionKey || "")
    .trim()
    .toLowerCase()
    .includes("admin");
}

function canChooseAirport(positionKey: unknown) {
  const normalized = String(positionKey || "")
    .trim()
    .toLowerCase();
  return isAdminPosition(normalized) || normalized === "manager";
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [adminAirports, setAdminAirports] = useState<AirportRecord[]>([]);
  const [adminAirportLoading, setAdminAirportLoading] = useState(false);
  const [, setAdminAirportsLoaded] = useState(false);
  const [adminAirportError, setAdminAirportError] = useState<string | null>(
    null,
  );
  const [adminAirportSelection, setAdminAirportSelection] =
    useState<AdminAirportSelection | null>(null);
  const [adminAirportDraftId, setAdminAirportDraftId] = useState("");

  const isAdminUser = useMemo(
    () => isAdminPosition(state.user?.positionKey),
    [state.user?.positionKey],
  );
  const isSuperAdminUser = useMemo(
    () => String(state.user?.positionKey || "").toLowerCase() === "super_admin",
    [state.user?.positionKey],
  );
  const isAirportChoosingUser = useMemo(
    () => canChooseAirport(state.user?.positionKey),
    [state.user?.positionKey],
  );
  const requiresAirportData = Boolean(
    state.isLoggedIn && isAirportChoosingUser,
  );

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
        const storedUser = readStoredUser();

        if (serviceToken && verifyToken(serviceToken) && storedUser) {
          if (!hasWebAppAccess(storedUser?.positionKey)) {
            setSession(null);
            setStoredUser(null);
            setStoredAdminAirportSelection(null);
            dispatch({ type: LOGOUT });
            return;
          }

          const storedAdminAirport = readStoredAdminAirportSelection();
          const storedUserIsSuperAdmin =
            String(storedUser?.positionKey || "").toLowerCase() ===
            "super_admin";
          if (
            canChooseAirport(storedUser?.positionKey) &&
            storedAdminAirport?.userId === storedUser?.id &&
            ((storedAdminAirport?.scope === "general" &&
              storedUserIsSuperAdmin) ||
              storedAdminAirport?.airportId)
          ) {
            setAdminAirportSelection(storedAdminAirport);
            setAdminAirportDraftId(
              storedAdminAirport.scope === "general"
                ? GENERAL_ADMIN_SCOPE_VALUE
                : String(storedAdminAirport.airportId || ""),
            );
          } else {
            setStoredAdminAirportSelection(null);
            setAdminAirportSelection(null);
            setAdminAirportDraftId("");
          }

          setSession(serviceToken);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: storedUser,
            },
          });
          return;
        }
      } catch (err) {
        console.error(err);
      }

      setSession(null);
      setStoredUser(null);
      setStoredAdminAirportSelection(null);
      dispatch({ type: LOGOUT });
    };

    void init();
  }, []);

  useEffect(() => {
    if (!state.isLoggedIn) return undefined;

    let timeoutId: number | undefined;

    const scheduleExpirationCheck = () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }

      const serviceToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!serviceToken) {
        redirectToLoginForAuthFailure();
        return;
      }

      try {
        const decoded: KeyedObject = jwtDecode(serviceToken);
        const expiresAtMs = Number(decoded.exp) * 1000;
        const remainingMs = expiresAtMs - Date.now();

        if (!Number.isFinite(expiresAtMs) || remainingMs <= 0) {
          redirectToLoginForAuthFailure();
          return;
        }

        timeoutId = window.setTimeout(
          scheduleExpirationCheck,
          Math.min(remainingMs, MAX_JAVASCRIPT_TIMEOUT_MS),
        );
      } catch {
        redirectToLoginForAuthFailure();
      }
    };

    const checkVisibleSession = () => {
      if (document.visibilityState === "visible") {
        scheduleExpirationCheck();
      }
    };

    scheduleExpirationCheck();
    window.addEventListener("focus", scheduleExpirationCheck);
    window.addEventListener(
      SESSION_TOKEN_CHANGED_EVENT,
      scheduleExpirationCheck,
    );
    document.addEventListener("visibilitychange", checkVisibleSession);

    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      window.removeEventListener("focus", scheduleExpirationCheck);
      window.removeEventListener(
        SESSION_TOKEN_CHANGED_EVENT,
        scheduleExpirationCheck,
      );
      document.removeEventListener("visibilitychange", checkVisibleSession);
    };
  }, [state.isLoggedIn, state.user?.id]);

  useEffect(() => {
    // The authentication initializer restores the persisted admin airport on
    // the first render. Do not let this cleanup effect race with that restore
    // while the auth state is still being initialized.
    if (!state.isInitialized) return;

    if (!requiresAirportData) {
      setAdminAirports([]);
      setAdminAirportLoading(false);
      setAdminAirportsLoaded(false);
      setAdminAirportError(null);
      setAdminAirportSelection(null);
      setAdminAirportDraftId("");
      return;
    }

    let mounted = true;
    const run = async () => {
      setAdminAirportLoading(true);
      setAdminAirportsLoaded(false);
      setAdminAirportError(null);
      try {
        const rows = await listAirports();
        if (!mounted) return;

        const sortedRows = [...rows]
          .filter((airport) => airport.isActive !== false)
          .sort((left, right) =>
            `${left.code || ""} ${left.name || ""}`.localeCompare(
              `${right.code || ""} ${right.name || ""}`,
            ),
          );

        setAdminAirports(sortedRows);
        if (
          !adminAirportSelection?.airportId &&
          !isAdminPosition(state.user?.positionKey)
        ) {
          const defaultAirport =
            sortedRows.find(
              (airport) => airport._id === state.user?.airportId,
            ) || sortedRows[0];
          if (defaultAirport && state.user?.id) {
            const nextSelection = {
              userId: state.user.id,
              scope: "airport" as const,
              airportId: defaultAirport._id,
              airportName:
                defaultAirport.name ||
                defaultAirport.code ||
                defaultAirport._id,
              airportTimezone: defaultAirport.timezone || null,
            };
            setAdminAirportSelection(nextSelection);
            setStoredAdminAirportSelection(nextSelection);
          }
        }
        setAdminAirportDraftId((previous) => {
          if (
            previous &&
            sortedRows.some((airport) => airport._id === previous)
          ) {
            return previous;
          }
          if (adminAirportSelection?.scope === "general" && isSuperAdminUser) {
            return GENERAL_ADMIN_SCOPE_VALUE;
          }
          if (
            adminAirportSelection?.airportId &&
            sortedRows.some(
              (airport) => airport._id === adminAirportSelection.airportId,
            )
          ) {
            return adminAirportSelection.airportId;
          }
          return "";
        });
      } catch (error) {
        if (!mounted) return;
        setAdminAirports([]);
        setAdminAirportError(
          error instanceof Error ? error.message : "Failed to load airports.",
        );
      } finally {
        if (mounted) {
          setAdminAirportLoading(false);
          setAdminAirportsLoaded(true);
        }
      }
    };

    void run();

    return () => {
      mounted = false;
    };
  }, [
    adminAirportSelection?.airportId,
    adminAirportSelection?.scope,
    requiresAirportData,
    state.isInitialized,
    state.user?.airportId,
    state.user?.id,
    state.user?.positionKey,
    isSuperAdminUser,
  ]);

  const login = async (employeeId: string, password: string) => {
    const response = await loginUser({ employee_id: employeeId, password });
    const serviceToken = response.token;
    const user = normalizeUser(response.user);
    if (!hasWebAppAccess(user.positionKey)) {
      setSession(null);
      setStoredUser(null);
      dispatch({ type: LOGOUT });
      throw new Error(WEB_APP_ACCESS_ERROR);
    }

    setSession(serviceToken);
    setStoredUser(user);
    setStoredAdminAirportSelection(null);
    setAdminAirportSelection(null);
    setAdminAirportDraftId("");
    setAdminAirportsLoaded(false);

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user,
      },
    });
  };

  const register = async (
    employeeId: string,
    password: string,
    firstName: string,
    lastName: string,
    options?: RegisterOptions,
  ) => {
    await registerUser({
      firstName,
      lastName,
      preferredName: options?.preferredName || firstName,
      employee_id: employeeId,
      email: options?.email,
      password,
      position: options?.position,
      airportId: options?.airportId,
    });
  };

  const logout = async () => {
    // Capture and send the authenticated revocation request before clearing the
    // local token, but never make local logout depend on network availability.
    void logoutUser().catch((error) => {
      console.warn("Failed to revoke the server session during logout", error);
    });

    setSession(null);
    setStoredUser(null);
    setStoredAdminAirportSelection(null);
    setAdminAirportSelection(null);
    setAdminAirportDraftId("");
    setAdminAirportsLoaded(false);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async () => {
    // The provided API set currently includes login/register only for auth flows.
    // Keep this method non-breaking for existing UI paths.
    return Promise.resolve();
  };

  const updateProfile = () => {
    const storedUser = readStoredUser();
    if (!storedUser) return;

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: storedUser,
      },
    });
  };

  const effectiveUser = useMemo(() => {
    if (!state.user) return state.user;
    if (!canChooseAirport(state.user.positionKey)) return state.user;
    if (!adminAirportSelection?.airportId) {
      return {
        ...state.user,
        airportId: null,
        airportName: null,
        airportTimezone: null,
      };
    }

    const matchedAirport = adminAirports.find(
      (airport) => airport._id === adminAirportSelection.airportId,
    );

    return {
      ...state.user,
      airportId: adminAirportSelection.airportId,
      airportName:
        matchedAirport?.name ||
        adminAirportSelection.airportName ||
        adminAirportSelection.airportId,
      airportTimezone:
        matchedAirport?.timezone ||
        adminAirportSelection.airportTimezone ||
        null,
    };
  }, [adminAirportSelection, adminAirports, state.user]);

  const adminAirportModalOpen = Boolean(
    state.isLoggedIn && isAdminUser && !adminAirportSelection,
  );

  const selectAirport = async (airportId: string) => {
    const selectedAirport = adminAirports.find(
      (airport) => airport._id === airportId,
    );
    if (!state.user?.id || !selectedAirport) {
      throw new Error("Select an airport you have access to.");
    }

    setAdminAirportLoading(true);
    setAdminAirportError(null);
    try {
      const response = await selectSessionAirport(selectedAirport._id);
      setSession(response.token);

      const nextSelection = {
        userId: state.user.id,
        scope: "airport" as const,
        airportId: selectedAirport._id,
        airportName:
          selectedAirport.name || selectedAirport.code || selectedAirport._id,
        airportTimezone: selectedAirport.timezone || null,
      };

      setAdminAirportDraftId(selectedAirport._id);
      setAdminAirportSelection(nextSelection);
      setStoredAdminAirportSelection(nextSelection);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to switch airports.";
      setAdminAirportError(message);
      throw error;
    } finally {
      setAdminAirportLoading(false);
    }
  };

  const selectGeneralScope = async () => {
    if (!state.user?.id || !isSuperAdminUser) {
      throw new Error(
        "General administration is available only to the super admin.",
      );
    }

    setAdminAirportLoading(true);
    setAdminAirportError(null);
    try {
      const response = await selectGeneralAdminSession();
      setSession(response.token);

      const nextSelection: AdminAirportSelection = {
        userId: state.user.id,
        scope: "general",
      };
      setAdminAirportDraftId(GENERAL_ADMIN_SCOPE_VALUE);
      setAdminAirportSelection(nextSelection);
      setStoredAdminAirportSelection(nextSelection);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to enter general administration.";
      setAdminAirportError(message);
      throw error;
    } finally {
      setAdminAirportLoading(false);
    }
  };

  const handleConfirmAdminAirport = () => {
    if (adminAirportDraftId === GENERAL_ADMIN_SCOPE_VALUE) {
      void selectGeneralScope().catch(() => undefined);
      return;
    }
    void selectAirport(adminAirportDraftId).catch(() => undefined);
  };

  const canSwitchAirport = Boolean(
    isAirportChoosingUser &&
      (adminAirports.length > 1 ||
        (isSuperAdminUser && adminAirports.length > 0)),
  );

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext
      value={{
        ...state,
        user: effectiveUser,
        login,
        logout,
        register,
        resetPassword,
        updateProfile,
        availableAirports: adminAirports,
        canSwitchAirport,
        airportSelectionLoading: adminAirportLoading,
        airportSelectionError: adminAirportError,
        selectAirport,
        selectGeneralScope,
      }}
    >
      <>
        {!adminAirportModalOpen ? children : null}
        <Dialog
          open={adminAirportModalOpen}
          fullWidth
          maxWidth="xs"
          disableEscapeKeyDown
        >
          <DialogTitle>Select workspace</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {adminAirportLoading ? <LinearProgress /> : null}
              <Typography variant="body2" color="text.secondary">
                Choose an airport for operational work, or enter General
                administration to create and configure airports.
              </Typography>
              {adminAirportError ? (
                <Alert severity="error">{adminAirportError}</Alert>
              ) : null}
              <TextField
                select
                fullWidth
                label="Workspace"
                value={adminAirportDraftId}
                onChange={(event) => setAdminAirportDraftId(event.target.value)}
                disabled={
                  adminAirportLoading ||
                  (adminAirports.length === 0 && !isSuperAdminUser)
                }
              >
                <MenuItem value="" disabled>
                  Select workspace
                </MenuItem>
                {isSuperAdminUser ? (
                  <MenuItem value={GENERAL_ADMIN_SCOPE_VALUE}>
                    General administration
                  </MenuItem>
                ) : null}
                {adminAirports.map((airport) => (
                  <MenuItem key={airport._id} value={airport._id}>
                    {airport.code} - {airport.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setAdminAirportError(null);
                setAdminAirports([]);
                setAdminAirportLoading(true);
                setAdminAirportsLoaded(false);
                void listAirports()
                  .then((rows) => {
                    const sortedRows = [...rows]
                      .filter((airport) => airport.isActive !== false)
                      .sort((left, right) =>
                        `${left.code || ""} ${left.name || ""}`.localeCompare(
                          `${right.code || ""} ${right.name || ""}`,
                        ),
                      );
                    setAdminAirports(sortedRows);
                  })
                  .catch((error) => {
                    setAdminAirportError(
                      error instanceof Error
                        ? error.message
                        : "Failed to load airports.",
                    );
                  })
                  .finally(() => {
                    setAdminAirportLoading(false);
                    setAdminAirportsLoaded(true);
                  });
              }}
              disabled={adminAirportLoading}
            >
              Reload
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmAdminAirport}
              disabled={!adminAirportDraftId || adminAirportLoading}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      </>
    </JWTContext>
  );
};

export default JWTContext;
